import React, { useMemo, useState } from 'react';
import { VIDEO_CHAPTERS, YOUTUBE_VIDEO_ID } from '../content/video';
import { useLang, T } from '../lib/lang';
import { motion } from 'framer-motion';

function fmtTime(totalSeconds: number) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

export function VideoSection() {
  const { lang } = useLang();
  const [startAt, setStartAt] = useState<number>(0);
  const [autoplayNonce, setAutoplayNonce] = useState<number>(0);

  const src = useMemo(() => {
    const params = new URLSearchParams();
    params.set('rel', '0');
    params.set('enablejsapi', '1');
    if (startAt > 0) params.set('start', String(startAt));
    if (autoplayNonce > 0) params.set('autoplay', '1');
    return `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?${params.toString()}`;
  }, [startAt, autoplayNonce]);

  return (
    <section id="video" className="border-b border-border py-20">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-dim">
            <T ru="video · founder os #14" en="video · founder os #14" />
          </div>
          <h2 className="mt-3 font-serif text-3xl font-light italic text-text md:text-4xl">
            <T ru="полный разговор" en="full conversation" />
          </h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-muted">
            <T
              ru="таймкоды собраны из глав видео. кликни на тему — и откроется нужный момент."
              en="timecodes come from the video chapters. click a topic to jump to that moment."
            />
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.35fr_1fr] lg:gap-10">
          <motion.div
            className="relative overflow-hidden rounded-lg border border-border bg-card"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <div className="relative w-full pb-[56.25%]">
              <iframe
                key={`${startAt}-${autoplayNonce}`}
                className="absolute inset-0 h-full w-full"
                src={src}
                title="Founder OS #14 — Alexey Ivanov"
                frameBorder={0}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>

          <motion.div
            className="rounded-lg border border-border bg-bg/30 p-4 backdrop-blur"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.05, ease: 'easeOut' }}
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-dim">
                <T ru="главы" en="chapters" />
              </div>
              <a
                href={`https://youtu.be/${YOUTUBE_VIDEO_ID}`}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] uppercase tracking-widest text-muted hover:text-text"
              >
                <T ru="youtube ↗" en="youtube ↗" />
              </a>
            </div>

            <div className="flex flex-col">
              {VIDEO_CHAPTERS.map((ch) => (
                <button
                  key={ch.start}
                  type="button"
                  onClick={() => {
                    setStartAt(ch.start);
                    setAutoplayNonce((x) => x + 1);
                  }}
                  className={[
                    'flex w-full items-start gap-4 border-t border-border px-2 py-3 text-left transition-colors',
                    'hover:bg-white/5',
                  ].join(' ')}
                >
                  <span className="mt-[1px] w-12 shrink-0 font-mono text-[11px] text-text">{fmtTime(ch.start)}</span>
                  <span className="text-[12px] leading-5 text-muted hover:text-text">
                    {lang === 'ru' ? ch.title.ru : ch.title.en}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
